import { useState, useEffect, useMemo, useRef } from 'react';
import { IconRefresh, IconSearch, IconLoader2, IconArrowLeft, IconDownload } from '@tabler/icons-react';
import api from '../lib/api';
import { readCampaignStats } from '@/features/campaign-stats/stats-read';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const CHANNEL_FILTERS = ['ALL', 'EMAIL', 'SMS', 'INBOX', 'PUSH'] as const;
type ChannelFilter = (typeof CHANNEL_FILTERS)[number];

const formatNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatCurrency = (num: number | null | undefined): string => {
  if (num === null || num === undefined || isNaN(num)) return 'R0.00';
  return `R${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** One channel's row in the campaigns list (contract §3.4 `channels[]`). */
interface CampaignChannelSummary {
  channel: string;
  deliveryStatsUnavailable?: boolean;
  deliveryStatsCheckedAt?: string;
  messages: number;
  sent: number;
  delivered: number;
}

interface Campaign {
  campaignId: string;
  deliveryStatsUnavailable?: boolean;
  messages: number;
  sent: number;
  delivered: number;
  deliveryRate: number;
  /** Empty on a campaign predating PR-8, or if the multi-channel fan-out failed. */
  channels?: CampaignChannelSummary[];
}

/** One channel's full totals block in the stats detail view (contract §3.4). */
interface CampaignChannelTotals {
  channel: string;
  deliveryStatsUnavailable?: boolean;
  deliveryStatsCheckedAt?: string;
  messages: number;
  totalSent: number;
  delivered: number;
  notDelivered: number;
  deliveredWithStatus: number;
  failed: number;
  pending: number;
  /** Percentage 0-100 with two decimals — never divide again. */
  deliveryRate: number;
  statusBreakdown: Array<{ status: string | null; count: number }>;
}

interface CampaignStats {
  campaignId: string;
  deliveryStatsUnavailable?: boolean;
  messages: number;
  totalSent: number;
  delivered: number;
  notDelivered: number;
  deliveredWithStatus: number;
  failed: number;
  pending: number;
  deliveryRate: number;
  conversions: number;
  conversionRate: number;
  conversionValue: number;
  statusBreakdown: Array<{ status: string | null; count: number }>;
  /** Per-channel breakdown (contract §3.4). Empty on a pre-PR-8 SMS-only campaign. */
  channels?: CampaignChannelTotals[];
  /**
   * Channels whose ledger lookup FAILED (not 404 — a real Nova error). Their
   * numbers are absent from every total on this page, so the totals are a
   * partial view and must be labelled as one. Absent on a pre-PR-8 backend.
   */
  channelsUnavailable?: string[];
}

/**
 * PUSH has no delivery receipts. EMAIL totals come from Brevo event reports.
 * Its accepted count remains available even without a delivery report.
 */
const CHANNELS_WITHOUT_DELIVERY_REPORTS = new Set(['PUSH']);

/**
 * BLOCKED is a strata QUEUE state and never reaches this page. Nova's ledger
 * status enum has no BLOCKED member, so `statusBreakdown` — which is Nova's
 * own row counts — structurally cannot contain one; a "Blocked" column here
 * read 0 for every campaign forever and quietly implied "nothing needs
 * reconciling". The real counts live in the queue DB (Send Jobs page + the
 * PR-8 reconciliation runbook); joining the two databases is out of scope for
 * this phase, so the page says where to look instead of faking a number.
 */
const BLOCKED_CAPTION =
  'BLOCKED recipients are a queue-level state and are not counted here — see the Send Jobs page and the reconciliation runbook.';

/**
 * What the Messages total actually counts. "Total SMS records" is only correct
 * for an SMS campaign — a single-channel EMAIL/PUSH/INBOX campaign counts that
 * channel's records, not SMS. The empty/absent case keeps the old wording on
 * purpose: that is a pre-PR-8 (or cached) response, which was SMS-only.
 */
function messagesCaption(channels: CampaignChannelTotals[] | undefined): string {
  if (channels && channels.length > 1) {
    return 'Across all channels';
  }
  if (channels && channels.length === 1) {
    return `Total ${channels[0].channel} records`;
  }
  return 'Total SMS records';
}

const getApiErrorMessage = (err: any, fallback: string): string => {
  if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
    return 'Campaign stats request timed out. Please try again.';
  }

  return err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || fallback;
};

export function CampaignStatsPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [campaignStats, setStats] = useState<CampaignStats | null>(null);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState('');
  const [statsError, setStatsError] = useState('');
  const [campaignSearch, setCampaignSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('ALL');
  const campaignsRequest = useRef<AbortController | null>(null);
  const detailRequest = useRef<AbortController | null>(null);

  const loadCampaigns = async () => {
    campaignsRequest.current?.abort();
    const controller = new AbortController();
    campaignsRequest.current = controller;
    setLoading(true);
    setError('');
    try {
      const response = await readCampaignStats(
        signal => api.get('/campaigns/stats', { signal }), controller.signal,
      );
      if (!controller.signal.aborted) setCampaigns(response.data || []);
    } catch (err: any) {
      if (!controller.signal.aborted) {
        setError(getApiErrorMessage(err, 'Failed to load campaigns'));
        setCampaigns([]);
      }
    } finally {
      if (campaignsRequest.current === controller && !controller.signal.aborted) setLoading(false);
    }
  };

  const loadCampaignStats = async (campaignId: string) => {
    detailRequest.current?.abort();
    const controller = new AbortController();
    detailRequest.current = controller;
    setLoadingStats(true);
    setStatsError('');
    try {
      const response = await readCampaignStats(
        signal => api.get(`/campaigns/stats/${encodeURIComponent(campaignId)}`, { signal }), controller.signal,
      );
      if (!controller.signal.aborted) setStats(response.data);
    } catch (err: any) {
      if (!controller.signal.aborted) {
        setStatsError(getApiErrorMessage(err, 'Failed to load campaign stats'));
        setStats(null);
      }
    } finally {
      if (detailRequest.current === controller && !controller.signal.aborted) setLoadingStats(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
    return () => campaignsRequest.current?.abort();
  }, []);

  useEffect(() => {
    if (selectedCampaign) {
      loadCampaignStats(selectedCampaign.campaignId);
    } else {
      setStats(null);
    }
    return () => detailRequest.current?.abort();
  }, [selectedCampaign]);

  const filteredCampaigns = useMemo(() => campaigns.flatMap((campaign) => {
    if (campaignSearch.trim() && !campaign.campaignId.toLowerCase().includes(campaignSearch.trim().toLowerCase())) return [];
    if (channelFilter === 'ALL') return [campaign];
    // Older responses without a breakdown contain SMS statistics only.
    const channels = campaign.channels?.length ? campaign.channels : [
      { channel: 'SMS', messages: campaign.messages, sent: campaign.sent, delivered: campaign.delivered },
    ];
    const selected = channels.find(channel => channel.channel === channelFilter);
    if (!selected) return [];
    return [{ ...campaign, ...selected, channels: [selected],
      deliveryStatsUnavailable: selected.deliveryStatsUnavailable,
      deliveryRate: selected.sent > 0 ? selected.delivered / selected.sent * 100 : 0 }];
  }), [campaigns, campaignSearch, channelFilter]);

  const stats = useMemo(() => {
    if (!campaignStats || channelFilter === 'ALL') return campaignStats;
    if (!campaignStats.channels?.length) return channelFilter === 'SMS' ? campaignStats : null;
    const channel = campaignStats.channels.find(channel => channel.channel === channelFilter);
    if (!channel) return null;
    return { ...campaignStats, ...channel, channels: [channel],
      deliveryStatsUnavailable: channel.deliveryStatsUnavailable,
      channelsUnavailable: campaignStats.channelsUnavailable?.filter(value => value === channelFilter) };
  }, [campaignStats, channelFilter]);

  const downloadReport = async () => {
    setExporting(true);
    try {
      const [{ jsPDF }, { autoTable }] = await Promise.all([
        import('jspdf'), import('jspdf-autotable'),
      ]);
      const doc = new jsPDF({ orientation: 'landscape' });
      let y = 18;
      const text = (value: string, size = 10) => {
        doc.setFontSize(size);
        for (const line of doc.splitTextToSize(value, 265)) {
          if (y > 180) { doc.addPage(); y = 18; }
          doc.text(line, 16, y);
          y += size * 0.45 + 2;
        }
      };
      const table = (head: string[], body: string[][]) => {
        autoTable(doc, {
          startY: y, head: [head], body, margin: { left: 16, right: 16, bottom: 20 },
          styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
          headStyles: { fillColor: [49, 79, 185] },
          alternateRowStyles: { fillColor: [245, 247, 251] },
        });
        y = (doc as typeof doc & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
      };
      doc.setTextColor(49, 79, 185);
      text('85 GROUP | STRATA', 18);
      doc.setTextColor(30, 30, 30);
      text('Campaign performance report', 15);
      text(`Channel: ${channelFilter === 'ALL' ? 'All channels' : channelFilter} | Search: ${campaignSearch.trim() || 'None'}`);
      text(`Generated: ${new Date().toISOString()} (UTC)`);
      text('Scope: currently loaded statistics. No brand or date filter is applied.');
      if (selectedCampaign && stats) {
        text(`Campaign: ${stats.campaignId}`, 12);
        if (stats.channelsUnavailable?.length) text(`Partial view: ${stats.channelsUnavailable.join(', ')} unavailable and excluded from totals.`);
        if (stats.deliveryStatsUnavailable) text('Email delivery statistics unavailable. This does not mean the emails were not delivered.');
        table(['Metric', 'Value'], [
          ['Messages', formatNumber(stats.messages)], ['Total sent (accepted for sending)', formatNumber(stats.totalSent)],
          ['Delivered', stats.deliveryStatsUnavailable ? 'Unavailable' : formatNumber(stats.delivered)],
          ['Delivery rate', stats.deliveryStatsUnavailable ? 'Unavailable' : `${stats.deliveryRate.toFixed(1)}%`],
          ['Conversions (purchases within 24h, ogr_commission = 1)', formatNumber(stats.conversions)],
          ['Conversion rate', `${stats.conversionRate.toFixed(2)}%`],
          ['Conversion value', formatCurrency(stats.conversionValue)],
          ['Failed', formatNumber(stats.failed)], ['Pending', formatNumber(stats.pending)],
        ]);
        if (stats.channels?.length) {
          text('Channel breakdown', 12);
          table(['Channel', 'Messages', 'Sent', 'Delivered', 'Failed', 'Pending', 'Delivery rate'], stats.channels.map(c => {
            const unavailable = c.deliveryStatsUnavailable ? 'Unavailable' : CHANNELS_WITHOUT_DELIVERY_REPORTS.has(c.channel) ? 'n/a' : null;
            return [c.channel, formatNumber(c.messages), formatNumber(c.totalSent), unavailable ?? formatNumber(c.delivered),
              formatNumber(c.failed), formatNumber(c.pending), unavailable ?? `${c.deliveryRate.toFixed(2)}%`];
          }));
          text(BLOCKED_CAPTION);
        }
        text('Status breakdown', 12);
        table(['Status', 'Count', 'Percentage'], stats.statusBreakdown.map(item => [
          item.status || 'NULL/Pending', formatNumber(item.count),
          `${stats.messages > 0 ? ((item.count / stats.messages) * 100).toFixed(1) : 0}%`,
        ]));
      } else {
        const sections = new Map<string, string[][]>();
        for (const campaign of filteredCampaigns) {
          const channels = campaign.channels?.length ? campaign.channels : [{
            channel: 'SMS', messages: campaign.messages, sent: campaign.sent, delivered: campaign.delivered,
            deliveryStatsUnavailable: campaign.deliveryStatsUnavailable,
          }];
          for (const channel of channels) {
            const unavailable = channel.deliveryStatsUnavailable ? 'Unavailable'
              : CHANNELS_WITHOUT_DELIVERY_REPORTS.has(channel.channel) ? 'n/a' : null;
            const rows = sections.get(channel.channel) ?? [];
            rows.push([campaign.campaignId, formatNumber(channel.messages), formatNumber(channel.sent),
              unavailable ?? formatNumber(channel.delivered),
              unavailable ?? `${(channel.sent > 0 ? channel.delivered / channel.sent * 100 : 0).toFixed(1)}%`]);
            sections.set(channel.channel, rows);
          }
        }
        for (const [channel, rows] of sections) {
          // Keep each heading with its table header and at least one row.
          if (y > 155) { doc.addPage(); y = 18; }
          text(`${channel} campaigns`, 12);
          table(['Campaign ID', 'Messages', 'Sent', 'Delivered', 'Delivery rate'], rows);
        }
      }
      const pages = doc.getNumberOfPages();
      for (let page = 1; page <= pages; page++) {
        doc.setPage(page);
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(`85 Group | Strata - Page ${page} of ${pages}`, 16, 202);
      }
      doc.save(`strata-campaign-report-${channelFilter.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch {
      toast.error('Could not generate the campaign report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Campaign Statistics"
        subtitle="View delivery statistics and performance metrics across all campaign channels (SMS, inbox, push, email)."
      />

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <Button onClick={loadCampaigns} disabled={loading}>
              <IconRefresh className="mr-2 h-4 w-4" />
              {loading ? 'Loading...' : 'Refresh Campaigns'}
            </Button>
            <Button variant="outline" onClick={downloadReport}
              disabled={exporting || loading || (selectedCampaign ? loadingStats || !stats || stats.campaignId !== selectedCampaign.campaignId : filteredCampaigns.length === 0)}>
              {exporting ? <IconLoader2 data-icon="inline-start" className="animate-spin" /> : <IconDownload data-icon="inline-start" />}
              {exporting ? 'Generating PDF...' : 'Download PDF'}
            </Button>
            <div className="relative min-w-[300px]">
              <IconSearch className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search campaigns..."
                value={campaignSearch}
                onChange={(e) => setCampaignSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span id="campaign-channel-filter-label" className="text-sm font-medium">Channel</span>
            <ToggleGroup
              aria-labelledby="campaign-channel-filter-label"
              className="flex-wrap"
              variant="outline"
              multiple={false}
              value={[channelFilter]}
              onValueChange={(values) => {
                const value = values[0] as ChannelFilter | undefined;
                if (!value) return;
                setChannelFilter(value);
                setSelectedCampaign(null);
              }}
            >
              {CHANNEL_FILTERS.map(channel => (
                <ToggleGroupItem key={channel} value={channel} aria-label={channel === 'ALL' ? 'All channels' : `${channel} campaigns`}>
                  {channel === 'ALL' ? 'All channels' : channel === 'EMAIL' ? 'Email' : channel === 'INBOX' ? 'Inbox' : channel === 'PUSH' ? 'Push' : 'SMS'}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription className="flex items-center justify-between">
                {error}
                <button onClick={() => setError('')} className="text-sm underline ml-4">Dismiss</button>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {loading && (
        <div className="flex justify-center p-8">
          <IconLoader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {!loading && campaigns.length > 0 && !selectedCampaign && (
        <MainCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-bold">Campaign ID</TableHead>
                <TableHead className="font-bold">Channels</TableHead>
                <TableHead className="text-right font-bold">Messages</TableHead>
                <TableHead className="text-right font-bold">Sent</TableHead>
                <TableHead className="text-right font-bold">Delivered</TableHead>
                <TableHead className="text-right font-bold">Delivery Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCampaigns.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No campaigns match the selected channel and search.
                </TableCell></TableRow>
              )}
              {filteredCampaigns.map((campaign) => (
                <TableRow
                  key={campaign.campaignId}
                  className="hover:bg-muted/50 cursor-pointer"
                  onClick={() => setSelectedCampaign(campaign)}
                >
                  <TableCell>{campaign.campaignId}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(campaign.channels ?? []).length === 0 ? (
                        <Badge variant="secondary">SMS</Badge>
                      ) : (
                        (campaign.channels ?? []).map((channel) => (
                          <Badge key={channel.channel} variant="secondary">
                            {channel.channel}
                          </Badge>
                        ))
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{formatNumber(campaign.messages)}</TableCell>
                  <TableCell className="text-right">{formatNumber(campaign.sent)}</TableCell>
                  <TableCell className="text-right">{campaign.deliveryStatsUnavailable ? 'Unavailable' : formatNumber(campaign.delivered)}</TableCell>
                  <TableCell className="text-right">
                    {campaign.deliveryStatsUnavailable ? <span className="text-muted-foreground">Unavailable</span> : <div className="flex items-center justify-end gap-2">
                      <div className="w-24 h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className={cn(
                            'h-full rounded-full',
                            campaign.deliveryRate >= 80 ? 'bg-green-500' :
                            campaign.deliveryRate >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                          )}
                          style={{ width: `${Math.min(campaign.deliveryRate, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-bold">{campaign.deliveryRate.toFixed(1)}%</span>
                    </div>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </MainCard>
      )}

      {selectedCampaign && (
        <div>
          <Button variant="outline" onClick={() => setSelectedCampaign(null)} className="mb-4">
            <IconArrowLeft className="mr-2 h-4 w-4" />
            Back to Campaigns List
          </Button>

          {loadingStats && (
            <div className="flex justify-center p-8">
              <IconLoader2 className="h-6 w-6 animate-spin" />
            </div>
          )}

          {statsError && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription className="flex items-center justify-between">
                {statsError}
                <button onClick={() => setStatsError('')} className="text-sm underline ml-4">Dismiss</button>
              </AlertDescription>
            </Alert>
          )}

          {stats && !loadingStats && (
            <div className="space-y-6">
              {stats.channelsUnavailable && stats.channelsUnavailable.length > 0 && (
                <Alert variant="warning">
                  <AlertDescription>
                    <strong>Partial view.</strong> {stats.channelsUnavailable.join(', ')} could not be
                    read from Nova, so {stats.channelsUnavailable.length > 1 ? 'those channels are' : 'that channel is'}{' '}
                    missing from every number below. Retry once Nova is healthy before drawing any
                    conclusion from these totals.
                  </AlertDescription>
                </Alert>
              )}
              {stats.deliveryStatsUnavailable && (
                <Alert><AlertDescription>Email delivery statistics are unavailable. Retry later; this does not mean the emails were not delivered.</AlertDescription></Alert>
              )}
              <div className="flex flex-wrap gap-4">
                <Card className="flex-[1_1_200px] min-w-[200px]">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-semibold mb-1">Messages</h3>
                    <p className="text-3xl font-bold text-primary">{formatNumber(stats.messages)}</p>
                    <span className="text-xs text-muted-foreground block mt-1">
                      {messagesCaption(stats.channels)}
                    </span>
                  </CardContent>
                </Card>
                <Card className="flex-[1_1_200px] min-w-[200px]">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-semibold mb-1">Total Sent</h3>
                    <p className="text-3xl font-bold text-primary">{formatNumber(stats.totalSent)}</p>
                    <span className="text-xs text-muted-foreground block mt-1">Accepted for sending</span>
                  </CardContent>
                </Card>
                <Card className="flex-[1_1_200px] min-w-[200px]">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-semibold mb-1">Delivered</h3>
                    <p className="text-3xl font-bold text-green-600">{stats.deliveryStatsUnavailable ? 'Unavailable' : formatNumber(stats.delivered)}</p>
                    <span className="text-sm text-muted-foreground">{stats.deliveryStatsUnavailable ? 'Provider report unavailable' : `${stats.deliveryRate.toFixed(1)}% delivery rate`}</span>
                    {!stats.deliveryStatsUnavailable && stats.deliveredWithStatus > 0 && (
                      <span className="text-xs text-muted-foreground block mt-1">
                        {formatNumber(stats.deliveredWithStatus)} confirmed delivered
                      </span>
                    )}
                  </CardContent>
                </Card>
                <Card className="flex-[1_1_200px] min-w-[200px]">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-semibold mb-1">Conversions</h3>
                    <p className="text-3xl font-bold text-blue-600">{formatNumber(stats.conversions)}</p>
                    <span className="text-sm text-muted-foreground">{stats.conversionRate.toFixed(2)}% conversion rate</span>
                    <span className="text-sm font-bold text-green-600 block mt-1">
                      {formatCurrency(stats.conversionValue)} total value
                    </span>
                    <span className="text-xs text-muted-foreground block mt-1">
                      Purchases within 24h (ogr_commission = 1)
                    </span>
                  </CardContent>
                </Card>
                <Card className="flex-[1_1_200px] min-w-[200px]">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-semibold mb-1">Failed</h3>
                    <p className="text-3xl font-bold text-destructive">{formatNumber(stats.failed)}</p>
                  </CardContent>
                </Card>
                <Card className="flex-[1_1_200px] min-w-[200px]">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-semibold mb-1">Pending</h3>
                    <p className="text-3xl font-bold text-yellow-600">{formatNumber(stats.pending)}</p>
                  </CardContent>
                </Card>
              </div>

              {stats.channels && stats.channels.length > 0 && (
                <MainCard>
                  <h3 className="text-lg font-semibold mb-4">Channel Breakdown</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-bold">Channel</TableHead>
                        <TableHead className="text-right font-bold">Messages</TableHead>
                        <TableHead className="text-right font-bold">Sent</TableHead>
                        <TableHead className="text-right font-bold">Delivered</TableHead>
                        <TableHead className="text-right font-bold">Failed</TableHead>
                        <TableHead className="text-right font-bold">Pending</TableHead>
                        <TableHead className="text-right font-bold">Delivery Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stats.channels.map((channel) => {
                        const deliveryUnavailable = CHANNELS_WITHOUT_DELIVERY_REPORTS.has(channel.channel) || channel.deliveryStatsUnavailable;
                        return (
                          <TableRow key={channel.channel}>
                            <TableCell>
                              <Badge variant="secondary">{channel.channel}</Badge>
                            </TableCell>
                            <TableCell className="text-right">{formatNumber(channel.messages)}</TableCell>
                            <TableCell className="text-right font-bold">{formatNumber(channel.totalSent)}</TableCell>
                            <TableCell className="text-right">
                              {deliveryUnavailable ? (
                                <span className="text-muted-foreground" title="Delivery report unavailable">
                                  {channel.deliveryStatsUnavailable ? 'Unavailable' : 'n/a'}
                                </span>
                              ) : (
                                formatNumber(channel.delivered)
                              )}
                            </TableCell>
                            <TableCell className="text-right">{formatNumber(channel.failed)}</TableCell>
                            <TableCell className="text-right">{formatNumber(channel.pending)}</TableCell>
                            <TableCell className="text-right">
                              {deliveryUnavailable ? (
                                <span className="text-muted-foreground">{channel.deliveryStatsUnavailable ? 'Unavailable' : 'n/a'}</span>
                              ) : (
                                `${channel.deliveryRate.toFixed(2)}%`
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <p className="text-xs text-muted-foreground mt-3">{BLOCKED_CAPTION}</p>
                </MainCard>
              )}

              <MainCard>
                <h3 className="text-lg font-semibold mb-4">Status Breakdown</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-bold">Status</TableHead>
                      <TableHead className="text-right font-bold">Count</TableHead>
                      <TableHead className="text-right font-bold">Percentage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.statusBreakdown.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {item.status ? (
                            <Badge
                              variant={
                                item.status === 'DELIVRD' ? 'success' :
                                item.status.includes('FAIL') || item.status.includes('ERROR') ? 'destructive' :
                                'secondary'
                              }
                            >
                              {item.status}
                            </Badge>
                          ) : (
                            <Badge variant="warning">NULL/Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{formatNumber(item.count)}</TableCell>
                        <TableCell className="text-right">
                          {stats.messages > 0 ? ((item.count / stats.messages) * 100).toFixed(1) : 0}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </MainCard>
            </div>
          )}
        </div>
      )}

      {!loading && campaigns.length === 0 && !error && (
        <MainCard className="text-center">
          <p className="text-muted-foreground">
            No campaigns found. Send your first campaign to see statistics here.
          </p>
        </MainCard>
      )}
    </div>
  );
}
